import React, { useEffect, useMemo, useRef, useState } from "react";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import { EditContext } from "../../context/EditContext";
import PreviewContext from "../../context/PreviewContext";
import { AddElementContext } from "../../context/AddElementContext";
import Background from "../../../renderer/components/Background";
import Button from "../../../renderer/components/Button";
import ButtonCollection from "../../../renderer/components/ButtonCollection";
import Component from "../../../renderer/components/Component";
import Hero from "../../../renderer/components/Hero";
import TextItem from "../../../renderer/components/Text";
import TitleItem from "../../../renderer/components/Title";
import useBackground from "../../../core/hooks/useBackground";
import useBaseStyle from "../../../core/hooks/useBaseStyle";
import useContainerStyle from "../../../core/hooks/useContainerStyle";
import useSpacing from "../../../core/hooks/useSpacing";
import { getFontFamilyClass, getFontFamilyStyle } from "../../../core/utils/characterStyleUtils";
import { transformColor } from "../../../core/utils/generalUtils";

const PREVIEW_CONFIG = {
  component: { scale: 0.56, minWidth: 760, minHeight: "19rem" },
  hero: { scale: 0.5, minWidth: 940, minHeight: "21rem" },
  title: { scale: 1, minWidth: 420, minHeight: "8rem" },
  text: { scale: 0.94, minWidth: 420, minHeight: "10rem" },
  button: { scale: 1, minWidth: 360, minHeight: "8rem" },
  button_collection: { scale: 0.76, minWidth: 700, minHeight: "14rem" },
  navbar: { scale: 0.78, minWidth: 900, minHeight: "12rem" },
  sidebar: { scale: 0.88, minWidth: 360, minHeight: "16rem" },
  navbar_section: { scale: 0.98, minWidth: 460, minHeight: "9rem" },
  sidebar_section: { scale: 0.98, minWidth: 360, minHeight: "10rem" },
  footer: { scale: 0.76, minWidth: 900, minHeight: "15rem" },
};

const PREVIEW_BG_CLASS = "bg-gray-50 dark:bg-dark-950";
const PREVIEW_VERTICAL_PADDING_PX = 40;

// Hex stage backgrounds chosen to maximize contrast against the previewed
// element while remaining neutral enough not to fight with the design.
const PREVIEW_STAGE_LIGHT_BG = "#fafafa";
const PREVIEW_STAGE_DARK_BG = "#0f0f10";

// Field names that contribute to the "ink" of the previewed element (text,
// icons, borders that read as foreground). Used when the element has no own
// surface — we contrast the stage against the ink instead.
const FOREGROUND_COLOR_KEYS = new Set([
  "text_color",
  "font_color",
  "icon_color",
]);

// Field names that fill the element's own surface. When present and opaque,
// the stage should contrast against THIS rather than against the text, so the
// element block stays visually distinct from the surrounding stage.
const SURFACE_COLOR_KEYS = new Set([
  "background_color",
  "fill_color",
  "container_color",
]);

const isMeaningfulColor = (raw) => {
  if (raw === null || raw === undefined) return false;
  if (typeof raw !== "string") return false;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return false;
  if (trimmed === "transparent" || trimmed === "none" || trimmed === "inherit") {
    return false;
  }
  return true;
};

const safeHexFor = (raw) => {
  if (!isMeaningfulColor(raw)) return null;
  try {
    const hex = transformColor(raw, 100, "hex");
    if (typeof hex !== "string" || !/^#[\da-f]{6}$/i.test(hex)) return null;
    return hex.toLowerCase();
  } catch (_) {
    return null;
  }
};

// Relative luminance per WCAG 2.x (sRGB → linearized → weighted sum).
// Returns a value in [0, 1]; 0 = pure black, 1 = pure white.
const hexLuminance = (hex) => {
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const toLinear = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const collectPreviewColors = (node, foregroundOut, surfaceOut) => {
  if (!node) return;
  if (Array.isArray(node)) {
    for (const item of node) collectPreviewColors(item, foregroundOut, surfaceOut);
    return;
  }
  if (typeof node !== "object") return;

  for (const [key, value] of Object.entries(node)) {
    if (value && typeof value === "object") {
      collectPreviewColors(value, foregroundOut, surfaceOut);
      continue;
    }
    const hex = safeHexFor(value);
    if (!hex) continue;
    if (FOREGROUND_COLOR_KEYS.has(key)) {
      foregroundOut.push(hex);
    } else if (SURFACE_COLOR_KEYS.has(key)) {
      surfaceOut.push(hex);
    }
  }
};

const averageLuminance = (hexes) => {
  if (!hexes.length) return null;
  let total = 0;
  for (const hex of hexes) total += hexLuminance(hex);
  return total / hexes.length;
};

/**
 * Pick a preview-stage background that gives strong contrast against the
 * previewed element. Falls back to the theme-aware default class when the
 * preview has no usable color information.
 *
 * Surface colors take priority over text colors: when the element has its own
 * background, that's what visually competes with the stage.
 */
const computePreviewStageStyle = (previewObject) => {
  const foreground = [];
  const surface = [];
  collectPreviewColors(previewObject, foreground, surface);

  const referenceLuminance = averageLuminance(surface) ?? averageLuminance(foreground);
  if (referenceLuminance === null) {
    // No usable color signal — keep the original theme-aware class.
    return { className: PREVIEW_BG_CLASS, style: null };
  }

  const stageBg = referenceLuminance < 0.5 ? PREVIEW_STAGE_LIGHT_BG : PREVIEW_STAGE_DARK_BG;
  return { className: "", style: { backgroundColor: stageBg } };
};

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Preview failures must never break the edit panel.
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={`${typography.bodySmall} ${textColors.muted}`}>
          Preview unavailable for this preset.
        </div>
      );
    }
    return this.props.children;
  }
}

function cloneJson(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
}

function ensurePreviewIds(value, prefix = "preset-preview") {
  if (Array.isArray(value)) {
    return value.map((item, index) => ensurePreviewIds(item, `${prefix}-${index}`));
  }

  if (value && typeof value === "object") {
    const next = Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        ensurePreviewIds(child, `${prefix}-${key}`),
      ]),
    );
    if (next.type && (next.id == null || next.id === "")) {
      next.id = prefix;
    }
    return next;
  }

  return value;
}

function preparePreviewObject(rawPreview, presetValue) {
  return ensurePreviewIds(cloneJson(rawPreview), `preset-${presetValue}`);
}

function getJustifyClass(position) {
  const normalized = String(position || "").toLowerCase();
  if (normalized === "center") return "justify-center";
  if (normalized === "right" || normalized === "end") return "justify-end";
  return "justify-start";
}

function isTailwindToken(value) {
  return typeof value === "string" && value.includes("-") && !value.startsWith("#");
}

function resolveTextColorProps(value) {
  if (!value) return { className: "", style: {} };
  if (isTailwindToken(value)) return { className: `text-${value}`, style: {} };
  return { className: "", style: { color: value } };
}

function NavBarSectionPreviewItem({ section }) {
  const { styleClasses, inlineStyles } = useBaseStyle(section);

  return (
    <div
      className={`relative inline-flex max-w-full items-center px-2 py-1 ${styleClasses}`}
      style={inlineStyles}
    >
      <TextItem item={section.label} returnDiv={false} />
    </div>
  );
}

function NavBarPreview({ item }) {
  const { styleClasses, inlineStyles } = useBaseStyle(item);
  const background = useBackground(item?.background || {});
  const sections = Array.isArray(item?.sections) ? item.sections : [];
  const titlePosition = String(item?.title_position || "center-above").toLowerCase();
  const titleAlign = titlePosition.split("-")[0] || "center";
  const showTitleAbove = titlePosition.includes("above");
  const sectionJustifyClass = getJustifyClass(item?.section_position);
  const titleJustifyClass = getJustifyClass(titleAlign);

  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl border ${borders.default} ${styleClasses}`}
      style={inlineStyles}
    >
      <Background
        bgImage={background.bgImageUrl}
        imageStyleClasses={background.imageStyleClasses}
        imageInlineStyles={background.imageInlineStyles}
        colorClass={background.bgPatternClass}
        colorStyle={background.bgPatternStyle}
        jsPatternLayer={background.bgPatternJsLayer}
      />
      <div className="relative z-10 flex min-h-[8.5rem] flex-col gap-4 p-4">
        {showTitleAbove && item?.title ? (
          <div className={`flex w-full ${titleJustifyClass}`}>
            <TitleItem item={item.title} returnDiv={false} />
          </div>
        ) : null}

        <div className={`flex w-full items-center gap-4 ${sectionJustifyClass}`}>
          {!showTitleAbove && item?.title ? (
            <div className="shrink-0">
              <TitleItem item={item.title} returnDiv={false} />
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {sections.map((section) => (
              <NavBarSectionPreviewItem key={section.id} section={section} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SideBarSectionPreviewItem({ section }) {
  const { styleClasses, inlineStyles } = useBaseStyle(section);
  const containerStyleClasses = useContainerStyle(section);
  const { padding } = useSpacing(section, false, null, null, true, false);
  const background = useBackground(section?.background || {});

  return (
    <div
      className={`relative w-full overflow-hidden ${padding} ${containerStyleClasses} ${styleClasses}`}
      style={inlineStyles}
    >
      <Background
        bgImage={background.bgImageUrl}
        imageStyleClasses={background.imageStyleClasses}
        imageInlineStyles={background.imageInlineStyles}
        colorClass={background.bgPatternClass}
        colorStyle={background.bgPatternStyle}
        jsPatternLayer={background.bgPatternJsLayer}
      />
      <div className="relative z-10 inline-flex max-w-full">
        <TextItem item={section.label} returnDiv={false} />
      </div>
    </div>
  );
}

function SideBarPreview({ item }) {
  const { styleClasses, inlineStyles } = useBaseStyle(item);
  const background = useBackground(item?.background || {});
  const sections = Array.isArray(item?.sections) ? item.sections : [];

  return (
    <aside
      className={`relative w-full overflow-hidden rounded-xl border ${borders.default} ${styleClasses}`}
      style={inlineStyles}
    >
      <Background
        bgImage={background.bgImageUrl}
        imageStyleClasses={background.imageStyleClasses}
        imageInlineStyles={background.imageInlineStyles}
        colorClass={background.bgPatternClass}
        colorStyle={background.bgPatternStyle}
        jsPatternLayer={background.bgPatternJsLayer}
      />
      <div className="relative z-10 flex min-h-[14rem] flex-col gap-3 p-4">
        {sections.map((section) => (
          <SideBarSectionPreviewItem key={section.id} section={section} />
        ))}
      </div>
    </aside>
  );
}

function FooterPreview({ item }) {
  const background = useBackground(item?.background || {});
  const { styleClasses, inlineStyles } = useBaseStyle(item);
  const highlightColorProps = resolveTextColorProps(item?.highlight_color);
  const copyrightColorProps = resolveTextColorProps(
    item?.copyright_text_color || item?.highlight_color,
  );
  const fontFamilyClass = getFontFamilyClass(item?.font_family);
  const fontFamilyStyle = getFontFamilyStyle(item?.font_family);

  return (
    <footer
      className={`relative w-full overflow-hidden rounded-xl border ${borders.default} ${styleClasses}`}
      style={inlineStyles}
    >
      <Background
        bgImage={background.bgImageUrl}
        imageStyleClasses={background.imageStyleClasses}
        imageInlineStyles={background.imageInlineStyles}
        colorClass={background.bgPatternClass}
        colorStyle={background.bgPatternStyle}
        jsPatternLayer={background.bgPatternJsLayer}
      />
      <div className="relative z-10 flex min-h-[13rem] flex-col justify-between gap-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div
              className={`${fontFamilyClass} ${copyrightColorProps.className} text-sm`}
              style={{ ...fontFamilyStyle, ...copyrightColorProps.style }}
            >
              {item?.copyright_text || "Copyright"}
            </div>
          </div>
          {item?.show_back_to_top ? (
            <div
              className={`rounded-full border border-current px-3 py-1 text-xs ${highlightColorProps.className}`}
              style={highlightColorProps.style}
            >
              Top
            </div>
          ) : null}
        </div>

        {item?.form_text ? (
          <div className="max-w-xl rounded-lg border border-white/20 bg-white/50 p-3 dark:bg-dark-900/40">
            <TextItem item={item.form_text} />
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          {item?.show_our_logo ? (
            <div className="rounded-full bg-white px-3 py-1 text-xs text-black">
              MyVitae
            </div>
          ) : (
            <span />
          )}
          <div
            className={`text-xs ${highlightColorProps.className}`}
            style={highlightColorProps.style}
          >
            {item?.show_form_to_send_message ? "Message form enabled" : "Message form hidden"}
          </div>
        </div>
      </div>
    </footer>
  );
}

function PreviewRenderer({ elementType, previewObject, containerWidth }) {
  if (elementType === "component") {
    return <Component item={previewObject} containerWidth={containerWidth} />;
  }
  if (elementType === "hero") {
    return <Hero item={previewObject} containerWidth={containerWidth} />;
  }
  if (elementType === "title") {
    return <TitleItem item={previewObject} />;
  }
  if (elementType === "text") {
    return <TextItem item={previewObject} />;
  }
  if (elementType === "button") {
    return <Button item={previewObject} />;
  }
  if (elementType === "button_collection") {
    return <ButtonCollection item={previewObject} containerWidth={containerWidth} />;
  }
  if (elementType === "navbar") {
    return <NavBarPreview item={previewObject} />;
  }
  if (elementType === "sidebar") {
    return <SideBarPreview item={previewObject} />;
  }
  if (elementType === "navbar_section") {
    return (
      <NavBarPreview
        item={{
          id: `${previewObject.id}-wrapper`,
          type: "navbar",
          section_position: "center",
          title_position: "center-above",
          show_our_logo: false,
          sections: [previewObject],
        }}
      />
    );
  }
  if (elementType === "sidebar_section") {
    return (
      <SideBarPreview
        item={{
          id: `${previewObject.id}-wrapper`,
          type: "sidebar",
          sections: [previewObject],
        }}
      />
    );
  }
  if (elementType === "footer") {
    return <FooterPreview item={previewObject} />;
  }
  return null;
}

export function PresetPreviewCard({
  preset,
  elementType,
  isApplying,
  isDisabled,
  onApply,
  scaleMultiplier = 1,
  verticalPaddingPx = PREVIEW_VERTICAL_PADDING_PX,
  // When true, the internal "<name> | Apply" header row is omitted. Used when a
  // parent (e.g. ElementStylePresetFamilyCard) renders a combined header above
  // the preview to save vertical space.
  hideHeader = false,
  // When true, the card renders as a non-interactive presentation block: no
  // outer button chrome (border, padding, role, hover, focus, click). Used when
  // the parent already provides the click target so we don't nest two buttons.
  chromeless = false,
}) {
  const previewObject = useMemo(
    () => preparePreviewObject(preset?.preview || {}, preset?.value || "preset"),
    [preset],
  );
  const config = PREVIEW_CONFIG[elementType] || PREVIEW_CONFIG.text;
  // Dynamic stage background: contrast against the previewed element so dark
  // elements get a near-white surface and light elements get a near-black one,
  // independent of the editor's light/dark theme.
  const stageStyle = useMemo(
    () => computePreviewStageStyle(previewObject),
    [previewObject],
  );
  const effectiveScale = useMemo(
    () => Math.max(0.18, Math.min(1, config.scale * scaleMultiplier)),
    [config.scale, scaleMultiplier],
  );
  const previewContentRef = useRef(null);
  const [previewHeight, setPreviewHeight] = useState(null);
  const previewContextValue = useMemo(
    () => ({
      mode: "responsive",
      isPresetStylePreview: true,
      contentWidth: config.minWidth,
      containerWidth: config.minWidth,
      viewport: { width: config.minWidth, height: 900 },
      onNavigate: () => {},
    }),
    [config.minWidth],
  );

  useEffect(() => {
    const contentNode = previewContentRef.current;
    if (!contentNode) return undefined;

    const updateHeight = () => {
      const rawHeight = contentNode.scrollHeight || contentNode.getBoundingClientRect().height || 0;
      if (!rawHeight) return;
      const nextHeight = Math.ceil(rawHeight * effectiveScale) + verticalPaddingPx;
      setPreviewHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
    };

    updateHeight();

    if (typeof ResizeObserver !== "function") return undefined;

    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    observer.observe(contentNode);
    return () => observer.disconnect();
  }, [effectiveScale, previewObject, verticalPaddingPx]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onApply?.();
    }
  };

  const previewBlock = (
    <div
      className={`relative flex items-start justify-center overflow-hidden rounded-lg border ${borders.default} ${stageStyle.className}`}
      style={{
        ...(previewHeight ? { height: previewHeight } : { minHeight: config.minHeight }),
        ...(stageStyle.style || {}),
      }}
    >
      <div
        className="pointer-events-none flex w-full justify-center py-5"
        style={{
          transform: `scale(${effectiveScale})`,
          transformOrigin: "top center",
          width: `${100 / effectiveScale}%`,
        }}
      >
        <PreviewErrorBoundary>
          <PreviewContext.Provider value={previewContextValue}>
            <AddElementContext.Provider value={{ enabled: true, openAddModal: () => {} }}>
              <EditContext.Provider value={null}>
                <div ref={previewContentRef} className="w-full">
                  <PreviewRenderer
                    elementType={elementType}
                    previewObject={previewObject}
                    containerWidth={config.minWidth}
                  />
                </div>
              </EditContext.Provider>
            </AddElementContext.Provider>
          </PreviewContext.Provider>
        </PreviewErrorBoundary>
      </div>
    </div>
  );

  // Chromeless: parent supplies the click target/border. Render preview only.
  if (chromeless) {
    return previewBlock;
  }

  return (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : onApply}
      onKeyDown={isDisabled ? undefined : handleKeyDown}
      className={`group rounded-xl border ${borders.default} ${surfaces.primary} p-3 transition-colors ${
        isDisabled
          ? "cursor-wait opacity-70"
          : `${surfaces.hover} cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500`
      }`}
    >
      {hideHeader ? null : (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className={`${typography.bodySmall} ${textColors.primary} truncate`}>
              {preset?.name || "Preset"}
            </div>
          </div>
          {isApplying ? (
            <div className={`${typography.caption} ${textColors.tertiary} shrink-0`}>
              Applying...
            </div>
          ) : null}
        </div>
      )}

      {previewBlock}
    </div>
  );
}
